import { Text } from "@/src/components/ui/BrandText";
import { surface, text as uiText, brand } from "@/src/theme/tokens";
import { Ionicons } from "@/src/components/ui/ionicons";
import React from "react";
import { Image, Pressable, ScrollView, View } from "react-native";

import type { PostPublishJob } from "../lib/postPublishTypes";
import { getPublicAuthorSnapshot } from "../lib/identity";
import { getSubjectLabel } from "../lib/taxonomy";
import { IdentityHeader } from "./IdentityHeader";

function pendingContext(job: PostPublishJob) {
  const subject = job.subject !== "general" ? getSubjectLabel(job.subject) : "";
  const destination = job.visibility === "global" ? "Fil général" : `Dans ${job.group?.groupName ?? "le groupe"}`;
  return [subject, destination].filter(Boolean).join(" · ");
}

function statusLabel(job: PostPublishJob) {
  if (job.status === "preparing") return "Préparation…";
  if (job.status === "queued") return "En attente d’envoi…";
  if (job.status === "uploading") {
    return job.progress.total > 0
      ? `Envoi ${job.progress.completed}/${job.progress.total}`
      : "Envoi en cours…";
  }
  if (job.status === "committing") return "Publication…";
  if (job.status === "awaiting-moderation") return "En attente de validation";
  if (job.status === "published") return "Publié";
  return "Échec de l'envoi";
}

export function PendingPostCard({
  job,
  onRetry,
  onCancel,
  onRemove,
}: {
  job: PostPublishJob;
  onRetry: () => void;
  onCancel: () => void;
  onRemove: () => void;
}) {
  const images = job.attachments.filter((item) => item.type === "image");
  const awaitingModeration = job.status === "awaiting-moderation" || job.status === "published";
  const canRetry = job.status === "failed" && job.error?.retryable !== false;
  const identity = getPublicAuthorSnapshot(job.author);

  return (
    <View style={{ borderRadius: 18, padding: 14, backgroundColor: surface.primary, borderWidth: 1, borderColor: awaitingModeration ? surface.warning : surface.selected, gap: 10 }}>
      <View style={{ flexDirection: "row", justifyContent: "space-between", gap: 10 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          {identity ? <IdentityHeader identity={identity} compact /> : null}
          {awaitingModeration ? (
            <View style={{ alignSelf: "flex-start", marginTop: 7, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 4, backgroundColor: surface.warning, borderWidth: 1, borderColor: brand.accent }}>
              <Text style={{ color: brand.accent, fontWeight: "900", fontSize: 12 }}>En révision</Text>
            </View>
          ) : null}
          <Text style={{ color: uiText.muted, marginTop: 7, flexShrink: 1 }}>{pendingContext(job)}</Text>
        </View>
        <Ionicons name={awaitingModeration ? "eye-off-outline" : "cloud-upload-outline"} size={20} color={awaitingModeration ? brand.accent : uiText.brand} />
      </View>

      {!!job.text && <Text style={{ color: uiText.primary, fontSize: 15, lineHeight: 20 }}>{job.text}</Text>}

      {images.length > 0 ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 10 }}>
          {images.map((image) => <Image key={image.attachmentId} source={{ uri: image.previewUri }} style={{ width: images.length > 1 ? 180 : 280, height: 220, borderRadius: 14, backgroundColor: surface.primary }} />)}
        </ScrollView>
      ) : null}

      <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <Text style={{ color: job.status === "failed" ? uiText.danger : awaitingModeration ? brand.accent : uiText.brand, fontWeight: "800", flex: 1 }}>{statusLabel(job)}</Text>
        {job.status === "failed" ? (
          <>
            {canRetry ? (
              <Pressable accessibilityRole="button" onPress={onRetry} style={{ minHeight: 44, justifyContent: "center" }}>
                <Text style={{ color: uiText.brand, fontWeight: "900" }}>Réessayer</Text>
              </Pressable>
            ) : null}
            <Pressable accessibilityRole="button" onPress={onRemove} style={{ minHeight: 44, justifyContent: "center" }}>
              <Text style={{ color: uiText.danger, fontWeight: "900" }}>Supprimer</Text>
            </Pressable>
          </>
        ) : !awaitingModeration ? (
          <Pressable onPress={onCancel} accessibilityRole="button" style={{ minHeight: 44, justifyContent: "center" }}><Text style={{ color: uiText.danger, fontWeight: "900" }}>Annuler</Text></Pressable>
        ) : null}
      </View>

      {awaitingModeration ? (
        <Text style={{ color: uiText.secondary, lineHeight: 19 }}>
          Publication enregistrée. Elle sera visible après validation.
        </Text>
      ) : null}

      {job.status === "uploading" && job.progress.total > 0 ? (
        <View style={{ height: 6, borderRadius: 999, overflow: "hidden", backgroundColor: surface.elevated }}>
          <View style={{ width: `${Math.round(job.progress.fraction * 100)}%`, height: "100%", backgroundColor: uiText.brand }} />
        </View>
      ) : null}

      {job.status === "failed" && job.error ? (
        <View style={{ gap: 3 }}>
          <Text style={{ color: uiText.muted }} numberOfLines={3}>
            {job.error.retryable
              ? "Connexion indisponible. Réessaie."
              : "Cette publication ne peut pas être envoyée."
            }
          </Text>
        </View>
      ) : null}
    </View>
  );
}
